import { useEffect, useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

const FOCUSABLE =
    'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab inside `ref`, moves focus in on mount (to an [autofocus] /
 * [data-autofocus] element, else the first focusable) and puts it back on
 * the previously focused element when the trap unmounts.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>) {
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const focusables = () =>
            Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);

        if (!el.contains(document.activeElement)) {
            const initial = el.querySelector<HTMLElement>("[data-autofocus]") ?? focusables()[0] ?? el;
            initial.focus({ preventScroll: true });
        }

        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const list = focusables();
            if (list.length === 0) {
                e.preventDefault();
                return;
            }
            const first = list[0]!;
            const last = list[list.length - 1]!;
            if (e.shiftKey && (document.activeElement === first || document.activeElement === el)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        el.addEventListener("keydown", onKey);
        return () => {
            el.removeEventListener("keydown", onKey);
            if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
        };
    }, [ref]);
}

type DialogSize = "sm" | "md" | "lg" | "xl";

const SIZES: Record<DialogSize, string> = {
    sm: "sm:max-w-sm",
    md: "sm:max-w-md",
    lg: "sm:max-w-lg",
    xl: "sm:max-w-2xl"
};

export interface DialogProps {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    children?: ReactNode;
    footer?: ReactNode;
    size?: DialogSize;
    /** When false, Esc / backdrop / X don't close (e.g. while busy). */
    dismissable?: boolean;
    className?: string;
}

/**
 * Accessible modal dialog: portal, focus trap, Esc + backdrop close, focus
 * restore. Bottom sheet on phones, centred card from `sm` up.
 */
export function Dialog(props: DialogProps) {
    if (!props.open) return null;
    return createPortal(<DialogInner {...props} />, document.body);
}

function DialogInner({ onClose, title, description, icon, children, footer, size = "md", dismissable = true, className }: DialogProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const descId = useId();
    useFocusTrap(panelRef);

    return (
        <div
            data-drive-overlay=""
            className="drive-fade-in fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && dismissable) onClose();
            }}
            onKeyDown={(e) => {
                // Keep keystrokes from reaching list/page shortcut handlers
                // (React bubbles portal events through the component tree).
                e.stopPropagation();
                if (e.key === "Escape" && dismissable) {
                    e.preventDefault();
                    onClose();
                }
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={description ? descId : undefined}
                tabIndex={-1}
                className={cn(
                    "drive-pop-in flex max-h-[90vh] w-full flex-col rounded-t-2xl border bg-card text-card-foreground shadow-2xl outline-none sm:rounded-xl",
                    SIZES[size],
                    className
                )}
            >
                <header className="flex items-start gap-3 px-5 pb-2 pt-4">
                    {icon ? <div className="mt-0.5 shrink-0">{icon}</div> : null}
                    <div className="min-w-0 flex-1">
                        <h2 id={titleId} className="break-words text-base font-semibold leading-snug">{title}</h2>
                        {description ? (
                            <p id={descId} className="mt-1 text-sm text-muted-foreground">{description}</p>
                        ) : null}
                    </div>
                    {dismissable ? (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="-mr-2 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : null}
                </header>
                {children ? <div className="min-h-0 flex-1 overflow-auto px-5 py-2">{children}</div> : null}
                {footer ? <footer className="flex flex-wrap justify-end gap-2 px-5 pb-4 pt-3">{footer}</footer> : null}
            </div>
        </div>
    );
}
